import * as tslib_1 from "tslib";
import { Location } from '@angular/common';
import { NavigationEnd, Router, } from '@angular/router';
import { ClientErrorCodes, ErrorTranslationKeys, FOLDER_ID_PARAM, FolderType, } from '@models';
import { FoldersService } from '@services/folders.service';
import { Logger } from '@services/logger/abstract.logger';
import { StorageService } from '@services/storage.service';
import { filterNullOrUndefined } from '@utils/rxjs.utils';
import { TranslatableError } from '@utils/translatable-error';
import { NullableStateManager } from 'main/app-state/generics/state-manager';
import * as queryString from 'query-string';
import { BehaviorSubject, iif, Observable, of, pipe, Subject, throwError, } from 'rxjs';
import { catchError, filter, map, switchMap, switchMapTo, takeUntil, tap, } from 'rxjs/operators';
var FOLDER_KEY = 'last-folder-id';
var HOST_PLACEHOLDER_FOLDER = {
    id: 0,
    displayName: '',
};
var ɵ0 = NaN;
var NO_FOLDER_PLACEHOLDER = {
    id: ɵ0,
    displayName: '',
};
/**
 * The current state is the user's current folder.
 */
var FolderManager = /** @class */ (function (_super) {
    tslib_1.__extends(FolderManager, _super);
    function FolderManager(_injector, _user) {
        var _this = _super.call(this) || this;
        _this._injector = _injector;
        _this._user = _user;
        _this._state = new BehaviorSubject(null);
        _this._storage = _this._injector.get(StorageService);
        _this._location = _this._injector.get(Location);
        _this._foldersService = _this._injector.get(FoldersService);
        // The Injector doesn't work directly with abstract classes, so manual casting to Type<Logger> has to be done.
        // More details: https://github.com/angular/angular/issues/23611
        _this._logStateChanges('Folder', _this._injector.get(Logger));
        _this._state
            .pipe(filterNullOrUndefined())
            .subscribe(function (folder) { return _this._storage.set(_this._folderKey, folder.id); });
        _this.type$ = _this.value$.pipe(map(function (folder) {
            if (!folder) {
                return null;
            }
            return folder.type;
        }));
        _this.isClassic$ = _this.type$.pipe(map(function (folderType) {
            return folderType == null || folderType === FolderType.Classic;
        }));
        return _this;
    }
    Object.defineProperty(FolderManager.prototype, "_router", {
        get: function () {
            // The router cannot be injected while the DI instantiates AppState for the App Initializer.
            // Otherwise, AppStateFacade would be injected as undefined inside the initializer function.
            if (!this._lazyInitializedRouter) {
                this._lazyInitializedRouter = this._injector.get(Router);
            }
            return this._lazyInitializedRouter;
        },
        enumerable: true,
        configurable: true
    });
    FolderManager.prototype.set = function (folderId) {
        var _this = this;
        return iif(function () { return !!_this._user.value; }, this._foldersService.getFolderContext(folderId, 1, 0)
            .pipe(tap(function (folderContext) {
            var folder = folderContext.folders[0];
            if (!folder) {
                throw new Error('NO_FOLDER');
            }
            folder.folderContext = folderContext;
            _this._state.next(folder);
        }), map(function () { return true; }), catchError(function () { return of(false); })), throwError(new TranslatableError(ErrorTranslationKeys.SessionExpired, ClientErrorCodes.SessionExpired)));
    };
    FolderManager.prototype.fetch = function () {
        var _this = this;
        this._watchOUQueryParam();
        return new Observable(function (subscriber) {
            if (!_this._user.value) {
                return subscriber.error(new TranslatableError(ErrorTranslationKeys.SessionExpired, ClientErrorCodes.SessionExpired));
            }
            var destroyed$ = new Subject();
            if (_this._user.loggedInAsHost) {
                _this._state.next(HOST_PLACEHOLDER_FOLDER);
                subscriber.next(HOST_PLACEHOLDER_FOLDER);
                return subscriber.complete();
            }
            var folderId = Number(_this._queryParamFolderId || _this._storageFolderId);
            var endStreamIfSucceeded = function () {
                return pipe(tap(function (success) {
                    if (success) {
                        subscriber.next(_this.value);
                        subscriber.complete();
                    }
                }), filter(function (success) { return !success; }));
            };
            var fallback$ = _this.set(null)
                .pipe(endStreamIfSucceeded(), tap(function () {
                _this._state.next(NO_FOLDER_PLACEHOLDER);
                subscriber.next(NO_FOLDER_PLACEHOLDER);
                subscriber.complete();
            }));
            iif(function () { return !!folderId; }, _this.set(folderId), of(false)).pipe(endStreamIfSucceeded(), switchMapTo(fallback$), takeUntil(destroyed$)).subscribe();
            return function () {
                destroyed$.next(true);
                destroyed$.complete();
            };
        });
    };
    FolderManager.prototype.clean = function () {
        var _this = this;
        return new Observable(function (subscriber) {
            _this._state.next(null);
            subscriber.next(null);
            subscriber.complete();
        });
    };
    Object.defineProperty(FolderManager.prototype, "hasAny", {
        get: function () {
            return !!this.value &&
                !isNaN(this.value.id);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FolderManager.prototype, "type", {
        get: function () {
            if (!this.value) {
                return null;
            }
            return this.value.type;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FolderManager.prototype, "_folderKey", {
        get: function () {
            return this._user.value ?
                FOLDER_KEY + "-" + (this._user.value.tenancyId || 'Default') + "-" + this._user.value.id :
                FOLDER_KEY + "-Default";
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FolderManager.prototype, "_storageFolderId", {
        get: function () {
            return this._storage.get(this._folderKey);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FolderManager.prototype, "_queryParamFolderId", {
        get: function () {
            var queryParams = queryString.parse(this._location.path().split('?')[1] || '');
            return Number(queryParams.ou) || null;
        },
        enumerable: true,
        configurable: true
    });
    FolderManager.prototype._watchOUQueryParam = function () {
        var _this = this;
        if (this._folderParamSub) {
            return;
        }
        this._folderParamSub = this._router.events
            .pipe(filter(function (ev) { return ev instanceof NavigationEnd; }), switchMap(function () { return _this._router.routerState.root.queryParamMap; }), map(function (queryParamMap) { return queryParamMap.get(FOLDER_ID_PARAM); }), filter(function (ouId) { return ouId !== null; }), map(Number), filter(function (ouId) { return ouId !== _this.value.id; }), switchMap(function () { return _this.fetch(); }))
            .subscribe();
    };
    return FolderManager;
}(NullableStateManager));
export { FolderManager };
export { ɵ0 };
